import { useEffect } from 'react';
import { useWorldStore } from '../state/worldStore';
import { useCityStore } from '../city/cityStore';
import { ROOMS } from '../rooms';
import { serverNow } from '../services/worldSocket';
import { audioEngine } from '../services/audioEngine';
import { api } from '../services/api';
import { setLevel } from '../city/hearing';
import { takeAudio, releaseAudio, onAudio } from '../services/audioPool';

// RoomSoundtrack — a room that plays its own station (2026-10-31).
//
// Khari: "In the warehouse club, please mute the outside radio when a user
// enters. It should be treated with its own soundtrack. … He is already the host
// of the rock radio station. Use that for the music there."
//
// Which room plays what is the server's (API city_rooms → the city snapshot's
// `rooms[<room id>]`: a `station`, or a `playlist`, migration 458 — the
// GangstaFuel Gym's), so the Blackroom, built on the same model, plays nothing
// of the Warehouse's. While you are in a room with a soundtrack:
//
//   · the outside radio is HELD (audioEngine.roomQuiet, holder 'room-station'),
//     faded out on the way in and back on the way out, like RoomSound's rooms;
//   · it plays from a pooled player, on the SERVER's clock (a station's own
//     playhead, or the playlist looped from the city's epoch: /now's seek_to_ms
//     either way), so everyone in the room hears the same bar of the same song —
//     CitySounds' way with a station;
//   · the player bar shows it (`soundNow`), and its ⏯ silences it (cityMusicOff);
//   · a set in this room (VenueStage, `venueNow`) plays instead, and so does a
//     station the listener tunes in by hand (RadioWheel): their choice.

const HOLDER = 'room-station';
const LEVEL = 0.9;
const TICK_MS = 500;
const RETRY_MS = 30000;   // nothing on (or the fetch failed): ask again this much later

export default function RoomSoundtrack() {
  const interior = useWorldStore((s) => s.interior);
  const station = useCityStore((s) => (interior && s.snap?.rooms?.[interior]?.station) || null);
  const playlist = useCityStore((s) => (interior && s.snap?.rooms?.[interior]?.playlist) || null);
  const stationName = useCityStore((s) => (interior && s.snap?.rooms?.[interior]?.stationName) || null);

  useEffect(() => {
    if (!interior || (!station && !playlist)) return undefined;
    const roomName = ROOMS[interior]?.name || '';
    let dead = false;
    let now = null;          // { url, title, artist, art, start, end } on the server clock
    let fetching = false;
    let retryAt = 0;
    let el = null; let elUrl = null; let started = false;
    let shown = null;        // the soundNow id we put up
    let nothing = false;     // the server said nothing is playing (an empty playlist)
    let held = false;
    // THE OUTSIDE RADIO IS HELD ONLY WHILE THE ROOM HAS SOMETHING OF ITS OWN
    // (2026-10-31: the gym's playlist started empty, and a held radio over an
    // empty playlist is a silent gym). A station is always on the air, so it
    // holds at once; a playlist holds when its first track arrives, and gives the
    // radio back whenever the server says there is nothing to play.
    const hold = (on) => {
      if (on === held) return;
      held = on;
      audioEngine.roomQuiet(on, 900, HOLDER).catch?.(() => {});
      console.info(`[room] ${interior} ${on ? `plays its own ${playlist ? `playlist (#${playlist})` : `station (${station})`}; the outside radio is held` : 'has nothing of its own playing; the outside radio is back'}`);
    };
    if (!playlist) hold(true);

    const fetchNow = () => {
      if (fetching || serverNow() < retryAt) return;
      fetching = true;
      (playlist ? api.roomNow(interior) : api.stationNow(station)).then((d) => {
        if (dead) return;
        const c = d?.current; const at = serverNow();
        if (!d?.playing || !c?.audio_url) { now = null; nothing = true; retryAt = at + RETRY_MS; return; }
        nothing = false;
        const dur = Number(c.duration_ms) || (Number(d.seek_to_ms) + Number(d.remaining_ms)) || 180000;
        const start = at - (Number(d.seek_to_ms) || 0);
        now = { url: c.audio_url, title: c.title || '', artist: c.artist || c.avatar_name || '', art: c.artwork_url || null, start, end: start + dur };
      }).catch(() => { retryAt = serverNow() + RETRY_MS; }).finally(() => { fetching = false; });
    };
    const stop = () => { if (el) { releaseAudio(el); el = null; elUrl = null; started = false; } };
    const show = (v) => {
      const cur = useCityStore.getState().soundNow;
      if (!v) { if (shown && cur?.id === shown) useCityStore.setState({ soundNow: null }); shown = null; return; }
      if (cur?.id !== v.id || cur?.where !== v.where) useCityStore.setState({ soundNow: v });
      shown = v.id;
    };

    const tick = () => {
      if (dead) return;
      const t = serverNow();
      if (!now || t >= now.end - 300) fetchNow();
      const cs = useCityStore.getState();
      const aside = (cs.venueNow?.room === interior && cs.venueNow?.live) || audioEngine.tunedInWhileHeld?.();
      if (nothing) { stop(); show(null); hold(false); return; }
      if (now) hold(true);
      if (aside || !now || t >= now.end) { stop(); show(null); return; }
      if (el && elUrl !== now.url) stop();   // the station moved on
      if (!el) {
        el = takeAudio(now.url); elUrl = now.url; started = false;
        try { el.load(); } catch { /* loads on play */ }
      }
      const off = (t - now.start) / 1000;
      const level = useWorldStore.getState().cityMusicOff ? 0 : LEVEL;
      setLevel(el, level);
      if (!started) {
        started = true;
        const cur = now; const mine = el;
        try { mine.currentTime = Math.max(0, off); } catch { /* on metadata */ }
        onAudio(mine, 'loadedmetadata', () => { try { mine.currentTime = Math.max(0, (serverNow() - cur.start) / 1000); } catch { /* ignore */ } }, { once: true });
        // Refused (no gesture yet on iOS, a blip): tried again on the next tick.
        mine.play().catch(() => { if (el === mine) started = false; });
      } else if (el.readyState >= 2 && Math.abs(el.currentTime - off) > 0.8) {
        try { el.currentTime = Math.max(0, off); } catch { /* ignore */ }
      }
      show({ id: `room:${interior}:${now.url}`, title: now.title, artist: now.artist, art: now.art, where: [stationName, roomName].filter(Boolean).join(' · ') });
    };

    tick();
    const timer = setInterval(tick, TICK_MS);
    return () => {
      dead = true;
      clearInterval(timer);
      stop();
      show(null);
      hold(false);
    };
  }, [interior, station, playlist, stationName]);

  return null;
}
